import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  dialogClasses,
} from '@mui/material';
import { post } from 'api';
import { url } from 'config/config';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';
import IconifyIcon from 'components/base/IconifyIcon';

const leaveSchema = yup.object().shape({
  leaveType: yup.string().required('Leave type is required'),

  fromDate: yup.date().required('From date is required').typeError('Invalid date'),

  toDate: yup
    .date()
    .required('To date is required')
    .min(yup.ref('fromDate'), 'To date cannot be before From date')
    .typeError('Invalid date'),

  quantity: yup.number().required('Quantity is required').min(1, 'Minimum 1 day required'),

  reason: yup
    .string()
    .required('Reason is required')
    .min(5, 'Reason must be at least 5 characters'),

  prescription: yup.mixed().when(['leaveType', 'quantity'], {
    is: (leaveType, quantity) => leaveType === 'SL' && quantity > 2,
    then: (schema) => schema.required('Prescription is required for Sick Leave more than 2 days'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const ApplyLeaveModal = ({ open, edit = null, handleClose }) => {
  const [preview, setPreview] = useState({
    image: null,
  });
  const [openImage, setOpenImage] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(leaveSchema),
    defaultValues: {
      leaveType: '',
      fromDate: '',
      toDate: '',
      quantity: '',
      reason: '',
      image: null,
    },
  });
  const handleFileChange = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      setPreview((prev) => ({
        ...prev,
        [field]: file,
      }));
    }
  };

  const submitHandler = async (data) => {
    // const res = await post('/admin/leave', { ...data });
    let res;
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof FileList) {
        if (value.length > 0) {
          formData.append(key, value[0]); // only take the first file
        }
      } else if (value instanceof Date) {
        formData.append(key, value.toISOString());
      } else {
        formData.append(key, value);
      }
    });
    if (edit) {
      // UPDATE
      res = await post(`/admin/leave/${edit._id}`, formData);
    } else {
      // CREATE
      res = await post('/admin/leave', formData);
    }
    if (res.status == 200) {
      enqueueSnackbar(
        edit ? 'Leave updated successfully' : 'Leave request submitted successfully',
        {
          variant: 'success',
          autoHideDuration: 3000,
        },
      );
      reset();
      handleClose();
    } else {
      enqueueSnackbar(res.message || 'Something went wrong', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };
  const leaveTypes = ['PL', 'SL', 'HL'];
  const isUpdating = useRef(false);

  const fromDate = watch('fromDate');
  const toDate = watch('toDate');
  // const quantity = watch('quantity');
  const leaveType = watch('leaveType');

  // 🔥 Auto calculate quantity
  //    🟢 1️⃣ Dates → Quantity
  useEffect(() => {
    if (!fromDate || !toDate) return;
    if (isUpdating.current) return;

    const start = new Date(fromDate);
    const end = new Date(toDate);

    if (end >= start) {
      isUpdating.current = true;

      const diff = (end - start) / (1000 * 60 * 60 * 24) + 1;

      setValue('quantity', diff, {
        shouldDirty: true,
        shouldValidate: true,
      });

      isUpdating.current = false;
    }
  }, [fromDate, toDate]);
  useEffect(() => {
    if (leaveType !== 'HL') return;
    if (!fromDate) return;
    if (isUpdating.current) return;
    isUpdating.current = true;

    const formattedDate = moment(fromDate).format('YYYY-MM-DD');

    setValue('toDate', formattedDate);
    setValue('quantity', 0.5, { shouldDirty: true });

    isUpdating.current = false;
  }, [leaveType, fromDate]);
  // 🟢 2️⃣ Quantity → ToDate
  // useEffect(() => {
  //   if (!fromDate || !quantity) return;
  //   if (isUpdating.current) return;

  //   const start = new Date(fromDate);

  //   isUpdating.current = true;

  //   if (quantity === 0.5) {
  //     // Half day → same date
  //     setValue('toDate', fromDate);
  //   } else if (quantity >= 1) {
  //     const newEnd = new Date(start);
  //     setValue(
  //       'toDate',
  //       moment(newEnd)
  //         .add('days', quantity - 1)
  //         .format('YYYY-MM-DD'),
  //     );
  //   }

  //   isUpdating.current = false;
  // }, [quantity]);
  useEffect(() => {
    if (edit) {
      reset({
        leaveType: edit.leaveType || '',
        fromDate: moment(edit.fromDate).format('YYYY-MM-DD'),
        toDate: moment(edit.toDate).format('YYYY-MM-DD'),
        quantity: edit.quantity,
        reason: edit.reason || '',
        image: edit.prescription ? `${url}${edit.prescription}` : null,
      });
      setPreview({ image: edit.prescription ? `${url}${edit.prescription}` : null });
    } else {
      reset({
        leaveType: '',
        fromDate: '',
        toDate: '',
        quantity: '',
        reason: '',
        image: null,
      });
      setPreview({ image: null });
    }
  }, [edit, reset]);
  // fullWidth maxWidth="sm"
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="dialog-title"
        component="form"
        onSubmit={handleSubmit(submitHandler)}
        sx={{
          [`& .${dialogClasses.paper}`]: {
            p: 0,
            borderRadius: 6,
            width: 1,
            maxWidth: 600,
          },
        }}
      >
        <DialogTitle
          component="h6"
          sx={{
            pt: 3,
            pb: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {edit ? 'Edit Leave' : 'Apply Leave'}
          <IconButton
            onClick={() => {
              reset();
              handleClose();
            }}
          >
            <IconifyIcon
              icon="material-symbols:close"
              sx={{ fontSize: 20, color: 'neutral.dark' }}
            />
          </IconButton>
        </DialogTitle>

        {/* <form onSubmit={handleSubmit(submitHandler)}> */}
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Leave Type */}
            <TextField
              select
              label="Leave Type"
              {...register('leaveType')}
              error={!!errors.leaveType}
              helperText={errors.leaveType?.message}
            >
              {leaveTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>

            {/* From Date */}
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  type="date"
                  label="From Date"
                  InputLabelProps={{ shrink: true }}
                  {...register('fromDate')}
                  error={!!errors.fromDate}
                  helperText={errors.fromDate?.message}
                  sx={{ width: '100%' }}
                />
              </Grid>

              {/* To Date */}
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  type="date"
                  label="To Date"
                  InputLabelProps={{ shrink: true }}
                  {...register('toDate')}
                  disabled={leaveType == 'HL'}
                  error={!!errors.toDate}
                  helperText={errors.toDate?.message}
                  sx={{ width: '100%' }}
                />
              </Grid>
            </Grid>

            {/* Quantity */}

            <TextField
              type="number"
              label="Number of Days"
              {...register('quantity')}
              InputLabelProps={{ shrink: true }}
              disabled={true}
              error={!!errors.quantity}
              helperText={errors.quantity?.message}
            />
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 3 }}>
                {' '}
                {preview?.image && (
                  <Box>
                    <img
                      src={
                        preview.image instanceof File
                          ? URL.createObjectURL(preview.image)
                          : preview.image
                      } // backend URL}
                      alt="Profile"
                      width="50%"
                      style={{ borderRadius: 8, cursor: 'pointer' }}
                      onClick={() => setOpenImage(true)}
                    />
                  </Box>
                )}
              </Grid>

              <Grid item size={{ xs: 12, sm: 9 }}>
                <Controller
                  name="prescription"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        sx={{ textTransform: 'none' }}
                      >
                        {preview?.image?.name || 'Upload Image'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            field.onChange(e.target.files);
                            handleFileChange('image', e);
                          }}
                        />
                      </Button>

                      <Typography color="error" variant="caption">
                        {errors.prescription?.message}
                      </Typography>
                    </>
                  )}
                />
              </Grid>
            </Grid>

            {/* Reason */}
            <TextField
              multiline
              rows={3}
              label="Reason"
              {...register('reason')}
              error={!!errors.reason}
              helperText={errors.reason?.message}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {edit ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
        {/* </form> */}
      </Dialog>
      <Dialog open={openImage} onClose={() => setOpenImage(false)} maxWidth="lg">
        <DialogContent sx={{ p: 0 }}>
          {preview?.image && (
            <img
              src={
                preview.image instanceof File ? URL.createObjectURL(preview.image) : preview.image
              }
              alt="Full Preview"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ApplyLeaveModal;
